import { useCallback, useState } from 'react';

import { useRefinementList } from 'react-instantsearch';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useSearchPageData } from 'src/app/pages/search/pageContext';
import {
	INSTANT_SEARCH_LOCATION_CITY_FILTER,
	INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
} from 'src/app/pages/search/shared/consts';

import { useResolveCityState } from './hooks/useResolveCityState';

type Result = ModelHookResult<
	{ isNearYouLoading: boolean },
	{
		filterByNearYou: () => void;
	}
>;

export function useFilterByNearYou(): Result {
	const {
		data: { viewer },
	} = useSearchPageData();
	const track = useTracking();

	const {
		actions: { resolveCityState },
	} = useResolveCityState();

	const { refine: filterByCountry } = useRefinementList({
		attribute: INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
	});

	const { refine: filterByCity } = useRefinementList({
		attribute: INSTANT_SEARCH_LOCATION_CITY_FILTER,
	});

	const [isNearYouLoading, setIsNearYouLoading] = useState(false);

	const filterByCityResolved = useCallback(
		async (city: string, countryCode: string, stateCode?: string) => {
			setIsNearYouLoading(true);
			const cityState = await resolveCityState({ city, countryCode, stateCode });
			filterByCity(cityState);
			setIsNearYouLoading(false);
		},
		[filterByCity, resolveCityState],
	);

	return {
		data: { isNearYouLoading },
		actions: {
			filterByNearYou: useCallback(() => {
				if (isNearYouLoading) return;

				void track('search', { q: '', context: 'landing_near_me_button' });

				const city = viewer.user?.city;
				const stateCode = viewer.user?.stateCode ? viewer.user.stateCode : undefined;
				const countryCode = viewer.user?.country?.countryCode || viewer.country.countryCode;

				if (city) {
					void filterByCityResolved(city, countryCode, stateCode);
					return;
				}
				filterByCountry(countryCode);
			}, [filterByCityResolved, filterByCountry, isNearYouLoading, track, viewer]),
		},
	};
}
